import { buildCalendarApiUrl } from '../config/apiEndpoints';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
};

export async function getCalendarHealth() {
  const response = await fetch(buildCalendarApiUrl('health'), { headers: authHeaders() });
  if (!response.ok) return null;
  return response.json();
}

export function getGoogleCalendarAuthUrl(userId) {
  const params = new URLSearchParams({ user_id: String(userId || '') });
  return buildCalendarApiUrl(`google/auth?${params.toString()}`);
}

/**
 * Open Google OAuth popup; resolves when calendar-sync posts occ-CalendarSyncResult.
 */
export function connectGoogleCalendar(userId) {
  const email = String(userId || '').trim();
  if (!email) {
    return Promise.reject(new Error('Google account email is required'));
  }

  const authParams = new URLSearchParams({
    user_id: email,
    use_full_calendar_scope: 'true'
  });
  const authEndpoint = `${buildCalendarApiUrl('google/auth')}?${authParams.toString()}`;

  return fetch(authEndpoint, { headers: authHeaders() }).then(async (authResponse) => {
    if (!authResponse.ok) {
      throw new Error(`Failed to get auth URL: ${authResponse.status}`);
    }
    const authData = await authResponse.json();
    let authUrl = authData.auth_url;
    if (!authUrl || typeof authUrl !== 'string') {
      throw new Error(authData.message || 'No authorization URL returned.');
    }
    if (authUrl.includes('accounts.google.com')) {
      if (authUrl.includes('prompt=')) {
        authUrl = authUrl.replace(/prompt=[^&]*/, 'prompt=consent');
      } else {
        authUrl += `${authUrl.includes('?') ? '&' : '?'}prompt=consent`;
      }
      if (!authUrl.includes('include_granted_scopes=')) {
        authUrl += '&include_granted_scopes=true';
      }
    }

    const popup = window.open(
      authUrl,
      'Google Calendar Authorization',
      'width=500,height=600,scrollbars=yes,resizable=yes'
    );
    if (!popup) {
      throw new Error('Popup blocked. Allow popups for this site and try again.');
    }

    const calendarOrigin = new URL(buildCalendarApiUrl('')).origin;

    const isTrustedOAuthMessage = (event, data) => {
      if (!data || data.type !== 'occ-CalendarSyncResult') return false;
      if (event.origin === calendarOrigin) return true;
      if (event.origin === window.location.origin) return true;
      try {
        const host = new URL(event.origin).hostname;
        if (host.endsWith('.amazonaws.com') && host.includes('execute-api')) return true;
        if (event.origin === 'https://app.occasia.com') return true;
      } catch {
        return false;
      }
      return false;
    };

    return new Promise((resolve, reject) => {
      let resolved = false;

      const finishSuccess = async () => {
        try {
          await listGoogleCalendars(email);
          resolve({ email, success: true });
        } catch {
          resolve({ email, success: true });
        }
      };

      const messageListener = (event) => {
        const data = event.data;
        if (!isTrustedOAuthMessage(event, data)) return;
        resolved = true;
        window.removeEventListener('message', messageListener);
        clearInterval(checkClosed);
        clearTimeout(timeoutId);
        if (popup && !popup.closed) popup.close();
        if (!data.success) {
          reject(new Error(data.message || 'Failed to connect Google Calendar'));
          return;
        }
        void finishSuccess();
      };

      window.addEventListener('message', messageListener);

      const checkClosed = setInterval(() => {
        if (popup.closed && !resolved) {
          setTimeout(async () => {
            if (resolved) return;
            try {
              await listGoogleCalendars(email);
              resolved = true;
              clearInterval(checkClosed);
              window.removeEventListener('message', messageListener);
              clearTimeout(timeoutId);
              resolve({ email, success: true });
            } catch {
              if (!resolved) {
                resolved = true;
                clearInterval(checkClosed);
                window.removeEventListener('message', messageListener);
                reject(
                  new Error(
                    'Google sign-in did not finish. Allow popups, complete consent, then try Connect Google again.'
                  )
                );
              }
            }
          }, 2500);
        }
      }, 500);

      const timeoutId = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          popup.close();
          clearInterval(checkClosed);
          window.removeEventListener('message', messageListener);
          reject(new Error('Authorization timed out'));
        }
      }, 300000);
    });
  });
}

export async function syncGoogleCalendarBusy({
  userId,
  venueId,
  days = 30,
  spaceId = null,
  saveToDb = true
} = {}) {
  const params = new URLSearchParams({
    user_id: String(userId || ''),
    venue_id: String(venueId || ''),
    provider: 'google',
    days: String(days),
    save_to_db: saveToDb ? 'true' : 'false'
  });
  if (spaceId) params.set('space_id', String(spaceId));
  const url = buildCalendarApiUrl(`busy?${params.toString()}`);
  const response = await fetch(url, { headers: authHeaders() });
  if (response.status === 401 || response.status === 403) {
    return { needsAuth: true, busyTimes: [] };
  }
  if (!response.ok) {
    throw new Error('Could not sync calendar');
  }
  const busyData = await response.json();
  let busyTimesList = [];
  if (Array.isArray(busyData)) busyTimesList = busyData;
  else if (Array.isArray(busyData.busy_times)) busyTimesList = busyData.busy_times;
  else if (busyData.success && Array.isArray(busyData.busy_times)) busyTimesList = busyData.busy_times;
  return { needsAuth: false, busyTimes: busyTimesList };
}

export async function listGoogleCalendars(userId) {
  const params = new URLSearchParams({ user_id: String(userId || '') });
  const response = await fetch(buildCalendarApiUrl(`google/calendars?${params.toString()}`), {
    headers: authHeaders()
  });
  if (!response.ok) {
    const detail = await response.text();
    const err = new Error(detail.includes('not connected') ? 'Google Calendar not connected for this email' : detail);
    err.status = response.status;
    err.rawDetail = detail;
    throw err;
  }
  const data = await response.json();
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.calendars)) return data.calendars;
  return data?.items ?? [];
}

/** Sync blocks to Google (write scopes required — not enabled in prod UI by default). */
export async function pushGoogleCalendar({ userId, venueId, days = 30, spaceId = null } = {}) {
  const params = new URLSearchParams({
    user_id: String(userId || ''),
    venue_id: String(venueId || ''),
    days: String(days)
  });
  if (spaceId) params.set('space_id', String(spaceId));
  const response = await fetch(buildCalendarApiUrl(`google/push?${params.toString()}`), {
    method: 'POST',
    headers: authHeaders()
  });
  return response;
}
